import { ExamCenterProctorRequestType } from '@exam-center/graphql';
import { dataGridSortDirection } from '@shared/components';
import { parseAsPageNumber } from '@shared/utils';
import {
  parseAsInteger,
  parseAsString,
  parseAsStringEnum,
  parseAsArrayOf,
  parseAsStringLiteral,
  useQueryStates,
} from 'nuqs';

import { type ProctorRequestStatus } from '../../types';

const PROCTOR_REQUEST_STATUSES = ['DRAFT', 'IN_REVIEW', 'REJECTED'] as const;

type SortOrder = (typeof dataGridSortDirection)[number];

export default function useProctorRequestListSearchParams() {
  const [
    { status, page, pageSize, sortOrder, sortBy, search, proctorType },
    setProctorRequestListSearchParams,
  ] = useQueryStates({
    status: parseAsStringLiteral(PROCTOR_REQUEST_STATUSES).withDefault('DRAFT'),
    page: parseAsPageNumber.withDefault(0),
    pageSize: parseAsInteger.withDefault(10),
    sortOrder: parseAsStringLiteral(dataGridSortDirection).withDefault('desc'),
    sortBy: parseAsString,
    search: parseAsString.withDefault(''),
    proctorType: parseAsArrayOf(
      parseAsStringEnum(Object.values(ExamCenterProctorRequestType)),
    ).withDefault([]),
  });

  const changePageNumber = (page: number) => {
    setProctorRequestListSearchParams({ page });
  };

  const changePageSize = (pageSize: number) => {
    setProctorRequestListSearchParams({ pageSize, page: 0 });
  };

  const changeStatus = (status: ProctorRequestStatus) => {
    setProctorRequestListSearchParams({ status, page: 0, pageSize: 10 });
  };

  const changeSearch = (search: string) => {
    setProctorRequestListSearchParams({ search, page: 0 });
  };

  const changeProctorType = (proctorType: ExamCenterProctorRequestType[]) => {
    setProctorRequestListSearchParams({ proctorType, page: 0 });
  };

  const changeSorting = ({
    sortOrder,
    sortBy,
  }: {
    sortOrder: SortOrder | null;
    sortBy: string | null;
  }) => {
    setProctorRequestListSearchParams({ sortOrder, page: 0, sortBy });
  };

  return [
    { status, page, pageSize, sortOrder, sortBy, search, proctorType },
    {
      changeStatus,
      changePageNumber,
      changePageSize,
      changeSorting,
      changeSearch,
      changeProctorType,
    },
  ] as const;
}
